/**
 * Upload file to MCP server and get back a real HTTP URL
 *
 * The MCP server provides file upload endpoint: POST /files/upload
 * which stores files in memory with 60-minute TTL and returns a public URL.
 */

export interface UploadFileParams {
  dataUrl: string;
  mimeType: string;
  filename: string;
}

export interface UploadFileResult {
  fileId: string;
  url: string;
}

/**
 * Get the MCP server URL from environment or default to localhost
 *
 * Environment variables:
 * - VITE_MCP_SERVER_URL: Set this at build time for production
 * - Development: Defaults to http://localhost:8000
 */
function getMcpServerUrl(): string {
  // Use environment variable if set (production/Railway)
  if (import.meta.env.VITE_MCP_SERVER_URL) {
    let url = import.meta.env.VITE_MCP_SERVER_URL;

    // If URL doesn't have protocol, add https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    console.log('[server-upload] Using MCP server URL:', url);
    return url;
  }

  // Development default
  console.log('[server-upload] Using default localhost:8000');
  return 'http://localhost:8000';
}

/**
 * Upload a file to the MCP server and get back a real HTTP URL
 *
 * @param params - File data including dataUrl, mimeType, and filename
 * @returns Promise with fileId and public URL
 */
export async function uploadFileToServer(
  params: UploadFileParams
): Promise<UploadFileResult> {
  const { dataUrl, mimeType, filename } = params;

  if (!dataUrl) {
    throw new Error('dataUrl is required');
  }

  // Extract base64 data from data URL
  // Format: data:image/png;base64,iVBORw0KGgoAAAANS...
  // Or with charset: data:text/csv;charset=utf-8;base64,iVBORw0KGgoAAAANS...
  const base64Match = dataUrl.match(/^data:[^,]*;base64,(.+)$/);
  if (!base64Match) {
    throw new Error(`Invalid data URL format. Expected "data:type;base64,..." but got: ${dataUrl.substring(0, 100)}`);
  }
  const base64Data = base64Match[1];

  const serverUrl = getMcpServerUrl();
  const uploadUrl = `${serverUrl}/files/upload`;

  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: base64Data,
      mimeType,
      filename,
    }),
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result as UploadFileResult;
}

/**
 * Helper to convert blob to data URL
 */
export async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Upload calendar data to create a shareable link
 *
 * @param calendarData - Calendar data with posts and month
 * @returns Promise with calendarId and shareable URL
 */
export async function uploadCalendarData(
  calendarData: { posts: any[]; month: string }
): Promise<{ calendarId: string; url: string }> {
  if (!calendarData.posts || !calendarData.month) {
    throw new Error('Calendar data must include posts and month');
  }

  const serverUrl = getMcpServerUrl();
  const uploadUrl = `${serverUrl}/calendars/create`;

  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(calendarData),
  });

  if (!response.ok) {
    throw new Error(`Calendar upload failed: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result as { calendarId: string; url: string };
}
